import { SavedChartState } from '../types';

// WebRTC Configuration with multiple STUN servers and free TURN servers
const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun2.l.google.com:19302' },
  { urls: 'stun:stun3.l.google.com:19302' },
  { urls: 'stun:stun4.l.google.com:19302' },
  // Additional public STUN servers
  { urls: 'stun:stun.stunprotocol.org:3478' },
  { urls: 'stun:stun.ekiga.net' },
  // Public TURN servers (these may have rate limits)
  { 
    urls: 'turn:openrelay.metered.ca:80',
    username: 'openrelayproject',
    credential: 'openrelayproject'
  },
  { 
    urls: 'turn:openrelay.metered.ca:443',
    username: 'openrelayproject', 
    credential: 'openrelayproject'
  }
];

// Message types for data channel communication
export interface WebRTCMessage {
  type: 'chart-update' | 'chart-full' | 'chart-state' | 'cursor-position' | 'client-join' | 'client-leave' | 'ping' | 'pong';
  data?: any;
  timestamp: number;
  senderId?: string;
}

export interface PeerInfo {
  id: string;
  name: string;
  connection: RTCPeerConnection;
  dataChannel?: RTCDataChannel;
  isConnected: boolean;
  lastSeen: number;
}

export type WebRTCRole = 'host' | 'client' | null;
export type ConnectionState = 'idle' | 'creating' | 'hosting' | 'joining' | 'connected' | 'disconnected' | 'error' | 'fallback-server';

export interface WebRTCCallbacks {
  onStateChange: (state: ConnectionState) => void;
  onPeerJoin: (peerInfo: PeerInfo) => void;
  onPeerLeave: (peerId: string) => void;
  onChartUpdate: (chartData: Partial<SavedChartState>) => void;
  onChartFullSync: (chartData: SavedChartState) => void;
  onError: (error: string) => void;
}

class WebRTCService {
  private role: WebRTCRole = 'client';
  private sessionCode: string = '';
  private localPeerId: string = '';
  private peers: Map<string, PeerInfo> = new Map();
  private callbacks: WebRTCCallbacks | null = null;
  private state: ConnectionState = 'idle';
  // Real signaling using HTTP polling
  private signalingUrl = '/api/signaling';
  private connectionId: string | null = null;
  private pollingInterval: number | null = null;
  private heartbeatInterval: number | null = null;
  private sessionStatusInterval: number | null = null;
  private fallbackMode: boolean = false;
  private fallbackTimeout: number | null = null;
  // Message deduplication - track processed message IDs to prevent stepping behavior
  private processedMessageIds: Set<string> = new Set();
  // Simple polling - tracking last message time for future optimization
  // @ts-ignore - variable is set but read is removed for now
  private lastMessageTime: number = 0;
  // Forced to edit-based only - no state switching
  private syncMode: 'edit-based' = 'edit-based';
  // Removed unused lastSuccessfulUpdate and stateBasedFallbackThreshold
  // Firebase Auth token for signaling authentication
  private authToken: string | null = null;
  // Track peers that have explicitly left - don't re-add them from stale server state (Vercel serverless issue)
  private leftPeers: Set<string> = new Set();
  // Chart sync heartbeat for fallback mode - periodically resend full chart to combat message loss
  private chartSyncInterval: number | null = null;
  private lastChartSyncCallback: (() => void) | null = null;

  constructor() {
    this.localPeerId = this.generatePeerId();
    // Removed initialization of deleted properties
  }

  public setAuthToken(token: string | null) {
    this.authToken = token;
  }

  private getAuthHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    };
    
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }
    
    return headers;
  }

  private generatePeerId(): string {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }

  private generateSessionCode(): string {
    return Math.random().toString(36).substr(2, 6).toUpperCase();
  }

  private updateState(newState: ConnectionState) {
    this.state = newState;
    this.callbacks?.onStateChange(newState);
    
    // Emit custom event for debugging
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('webrtc-state-change', { 
        detail: { state: newState } 
      }));
    }
  }

  private createPeerConnection(): RTCPeerConnection {
    const pc = new RTCPeerConnection({ 
      iceServers: ICE_SERVERS,
      iceCandidatePoolSize: 10,
      iceTransportPolicy: 'all' // Allow all transport policies including TURN
    });

    pc.oniceconnectionstatechange = () => {
      console.log('ICE connection state:', pc.iceConnectionState);
      console.log('ICE gathering state:', pc.iceGatheringState);
      console.log('Connection state:', pc.connectionState);
      
      if (pc.iceConnectionState === 'disconnected' || pc.iceConnectionState === 'failed') {
        console.log('❌ ICE connection failed - possible NAT/firewall issues');
        console.log('💡 Consider using a dedicated TURN service for better connectivity');
        console.log('🔄 Falling back to server-based collaboration...');
        
        // Activate fallback mode
        this.activateFallbackMode();
        
        // DON'T remove the peer in fallback mode - they're still connected via server
        // Only remove if we're not in fallback mode (which shouldn't happen, but just in case)
        if (!this.fallbackMode) {
          this.handlePeerDisconnection(pc);
        } else {
          console.log('📡 In fallback mode - keeping peer connected via server');
        }
      } else if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
        console.log('✅ ICE connection successful!');
      }
    };

    pc.onconnectionstatechange = () => {
      console.log('Connection state:', pc.connectionState);
    };

    pc.onicegatheringstatechange = () => {
      console.log('ICE gathering state:', pc.iceGatheringState);
    };

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        const candidate = event.candidate;
        console.log(`🔍 Local ICE candidate: ${candidate.type} | ${candidate.protocol} | ${candidate.address || 'hidden'}:${candidate.port || 'hidden'} | priority: ${candidate.priority}`);
        console.log(`🔍 Full candidate: ${candidate.candidate}`);
      } else {
        console.log('🔍 ICE gathering complete');
      }
    };

    return pc;
  }

  private setupDataChannel(channel: RTCDataChannel, peerId: string) {
    console.log(`📡 ${this.role}: Setting up data channel with peer ${peerId}`);
    
    channel.onopen = () => {
      console.log(`✅ ${this.role}: Data channel opened with peer ${peerId}`);
      
      // Clear fallback timeout since P2P connection is working
      this.clearFallbackTimeout();
      
      const peer = this.peers.get(peerId);
      if (peer) {
        peer.isConnected = true;
        peer.lastSeen = Date.now();
        
        // If we're the host and this is our first peer, move to connected state
        if (this.role === 'host' && this.state === 'hosting') {
          console.log(`🟢 Host: First client connected, moving to connected state`);
          this.updateState('connected');
        }
        // If we're a client and we just connected, move to connected state
        else if (this.role === 'client' && this.state === 'joining') {
          console.log(`🔵 Client: Connected to host, moving to connected state`);
          this.updateState('connected');
        }
        
        this.callbacks?.onPeerJoin(peer);
      }
    };

    channel.onmessage = (event) => {
      try {
        const message: WebRTCMessage = JSON.parse(event.data);
        console.log(`📩 ${this.role}: Received data message:`, message.type);
        this.handleDataChannelMessage(message, peerId);
      } catch (error) {
        console.error('Failed to parse data channel message:', error);
      }
    };

    channel.onclose = () => {
      console.log(`❌ ${this.role}: Data channel closed with peer ${peerId}`);
      // In fallback mode, don't remove peer when data channel closes
      // The peer is still connected via signaling server
      if (!this.fallbackMode) {
        this.removePeer(peerId);
      } else {
        console.log(`📡 In fallback mode - keeping peer ${peerId} connected via server`);
      }
    };

    channel.onerror = (error) => {
      console.error(`💥 ${this.role}: Data channel error with peer ${peerId}:`, error);
      this.callbacks?.onError(`Data channel error with peer ${peerId}`);
    };
  }

  private handleDataChannelMessage(message: WebRTCMessage, senderId: string) {
    console.log(`🔧 handleDataChannelMessage called with type: ${message.type}, senderId: ${senderId}`);
    
    // Update peer last seen
    const peer = this.peers.get(senderId);
    if (peer) {
      peer.lastSeen = Date.now();
    }

    // Update received successfully
    
    // Always stay in edit-based mode - removed state switching complexity

    switch (message.type) {
      case 'chart-update':
        // Always handle chart updates - no more mode checking
        console.log('🔧 Received chart update');
        this.callbacks?.onChartUpdate(message.data);
        break;
      case 'chart-full':
        console.log('🔧 Received full chart sync');
        this.callbacks?.onChartFullSync(message.data);
        break;
      case 'chart-state':
        // Handle state messages as full sync too - simplified
        console.log('🔧 Received chart state (treating as full sync)');
        this.callbacks?.onChartFullSync(message.data);
        break;
      case 'ping':
        this.sendMessage({ type: 'pong' }, senderId);
        break;
      case 'pong':
        // Peer is alive, already updated lastSeen
        break;
      default:
        console.log('Unknown message type:', message.type);
    }
  }

  private removePeer(peerId: string) {
    const peer = this.peers.get(peerId);
    if (peer) {
      if (peer.connection) {
        try { peer.connection.close(); } catch (e) { /* ignore */ }
      }
      this.peers.delete(peerId);
      // Track that this peer left - don't re-add from stale server state
      this.leftPeers.add(peerId);
      console.log(`👋 Peer ${peerId} added to leftPeers set (${this.leftPeers.size} total)`);
      this.callbacks?.onPeerLeave(peerId);
    } else {
      // Even if peer wasn't in our list, track that they left
      this.leftPeers.add(peerId);
      console.log(`👋 Peer ${peerId} (unknown) added to leftPeers set (${this.leftPeers.size} total)`);
      this.callbacks?.onPeerLeave(peerId);
    }
  }

  private handlePeerDisconnection(connection: RTCPeerConnection) {
    // Find peer by connection
    for (const [peerId, peer] of this.peers.entries()) {
      if (peer.connection === connection) {
        this.removePeer(peerId);
        break;
      }
    }
  }

  private startHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }

    this.heartbeatInterval = window.setInterval(() => {
      const now = Date.now();
      
      // In fallback mode, use 45 second timeout (3x the heartbeat interval of 15s)
      // This gives enough buffer for message delays while still detecting disconnects reasonably fast
      const timeout = this.fallbackMode ? 45000 : 30000; // 45s in fallback, 30s otherwise

      // Send pings and check for timeouts
      for (const [peerId, peer] of this.peers.entries()) {
        const timeSinceLastSeen = now - peer.lastSeen;
        
        if (timeSinceLastSeen > timeout) {
          console.log(`⏰ Peer ${peerId} timed out (${Math.round(timeSinceLastSeen / 1000)}s since last seen)`);
          this.removePeer(peerId);
        } else if (!this.fallbackMode && peer.isConnected && peer.dataChannel?.readyState === 'open') {
          this.sendMessage({ type: 'ping' }, peerId);
        }
      }
      
      // In fallback mode, send heartbeat via signaling to keep lastSeen updated on host
      if (this.fallbackMode && this.role === 'client') {
        this.sendSignalingMessage({
          type: 'heartbeat',
          peerId: this.localPeerId
        });
      }
    }, 15000); // Check every 15 seconds
  }

  private stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private activateFallbackMode() {
    console.log('🔄 Activating server-based fallback mode for collaboration');
    this.fallbackMode = true;
    this.clearFallbackTimeout(); // Clear the timeout since we're now in fallback mode
    this.updateState('fallback-server');
    
    // DISABLED: Session status polling causes intermittent ejection/reconnection on Vercel serverless
    // because each serverless instance has different in-memory state for session.clients
    // Instead, we rely solely on signaling messages (join/leave) for peer tracking
    // this.startSessionStatusPolling();
    
    // In fallback mode, we'll send chart updates through the signaling server
    // instead of direct P2P data channels
    
    // For hosts: start chart sync heartbeat to combat Vercel serverless message loss
    if (this.role === 'host') {
      this.startChartSyncHeartbeat();
    }
    
    // For clients entering fallback mode, request initial chart data from host
    if (this.role === 'client') {
      console.log('📡 Client in fallback mode: requesting initial chart data');
      this.sendSignalingMessage({
        type: 'chart-request',
        data: { requestFullChart: true }
      });
    }
  }
  
  // Chart sync heartbeat - host periodically broadcasts full chart to combat message loss
  private startChartSyncHeartbeat() {
    if (this.chartSyncInterval) {
      clearInterval(this.chartSyncInterval);
    }
    
    console.log('📡 Host: Starting chart sync heartbeat (every 15 seconds)');
    this.chartSyncInterval = window.setInterval(() => {
      if (this.role === 'host' && this.fallbackMode && this.lastChartSyncCallback) {
        console.log('📡 Host: Sending periodic chart sync heartbeat');
        this.lastChartSyncCallback();
      }
    }, 15000); // Every 15 seconds, resend full chart
  }
  
  private stopChartSyncHeartbeat() {
    if (this.chartSyncInterval) {
      clearInterval(this.chartSyncInterval);
      this.chartSyncInterval = null;
    }
  }
  
  // Called by App.tsx to register the chart sync callback
  public setChartSyncCallback(callback: () => void) {
    this.lastChartSyncCallback = callback;
  }

  private startFallbackTimeout() {
    // Clear any existing timeout
    this.clearFallbackTimeout();
    
    // Start 5-second timeout for fallback mode (reduced for faster access)
    console.log('⏰ Starting 5-second fallback timeout');
    this.fallbackTimeout = window.setTimeout(() => {
      if (!this.fallbackMode && (this.state === 'joining' || this.state === 'hosting')) {
        console.log('⏰ 5 seconds elapsed, activating fallback mode');
        this.activateFallbackMode();
      }
    }, 5000);
  }

  private clearFallbackTimeout() {
    if (this.fallbackTimeout) {
      clearTimeout(this.fallbackTimeout);
      this.fallbackTimeout = null;
    }
  }

  // DISABLED: Session status polling causes issues on Vercel serverless
  // Each instance has different in-memory state, causing intermittent ejection/reconnection
  // Keeping the code for reference but not using it
  // @ts-ignore - intentionally unused
  private _startSessionStatusPolling() {
    if (this.sessionStatusInterval || !this.sessionCode || !this.fallbackMode) return;
    
    console.log(`👥 Starting session status polling for ${this.sessionCode}`);
    
    this.sessionStatusInterval = window.setInterval(async () => {
      try {
        await this._pollSessionStatus();
      } catch (error) {
        console.error('❌ Session status polling error:', error);
      }
    }, 5000); // Poll every 5 seconds
  }

  private stopSessionStatusPolling() {
    if (this.sessionStatusInterval) {
      clearInterval(this.sessionStatusInterval);
      this.sessionStatusInterval = null;
    }
  }

  // @ts-ignore - intentionally unused (part of disabled session status polling)
  private async _pollSessionStatus() {
    if (!this.sessionCode) return;

    try {
      const response = await fetch(`${this.signalingUrl}?action=get-session-status&sessionCode=${this.sessionCode}`, {
        headers: this.getAuthHeaders()
      });
      
      if (!response.ok) {
        console.warn(`⚠️ Session status request failed: ${response.status}`);
        return;
      }

      const status = await response.json();
      // Session status received - only log on changes
      // console.log(`👥 Session status:`, status);

      // Update peer tracking with real data from signaling server
      this._updatePeersFromSessionStatus(status);

    } catch (error) {
      console.error('❌ Failed to poll session status:', error);
    }
  }

  // @ts-ignore - intentionally unused (part of disabled session status polling)
  private _updatePeersFromSessionStatus(status: any) {
    // In fallback mode with Vercel serverless, the server state is unreliable
    // (different instances have different in-memory state)
    // So we should NOT remove peers based on server list - only trust explicit 'leave' messages
    // We only ADD new peers from server, never remove existing ones

    if (this.role === 'host') {
      // DON'T remove peers based on server list - Vercel serverless makes this unreliable
      // Peers are only removed via explicit 'leave' message (handled in handleSignalingMessage)
      
      // Add new clients from server, but SKIP those in leftPeers set
      let addedCount = 0;
      let skippedCount = 0;
      status.clients.forEach((client: any) => {
        // Check if this peer explicitly left - don't re-add them
        if (this.leftPeers.has(client.id)) {
          console.log(`👥 Host: Skipping peer ${client.id} - they explicitly left (stale server data)`);
          skippedCount++;
          return;
        }
        
        if (!this.peers.has(client.id)) {
          const peerInfo: PeerInfo = {
            id: client.id,
            name: client.name,
            connection: null as any, // Fallback mode doesn't use real connections
            isConnected: true,
            lastSeen: Date.now(), // Use current time, not stale server data
            dataChannel: undefined
          };
          this.peers.set(client.id, peerInfo);
          this.callbacks?.onPeerJoin?.(peerInfo);
          addedCount++;
        } else {
          // Peer exists - refresh their lastSeen since they're still in the session
          const existingPeer = this.peers.get(client.id);
          if (existingPeer) {
            existingPeer.lastSeen = Date.now();
          }
        }
      });
      
      console.log(`👥 Host: Peer update - ${this.peers.size} connected, ${addedCount} added, ${skippedCount} skipped (left)`);
    } else if (this.role === 'client') {
      // Client sees host - only update if not already connected
      if (status.hasHost && status.hostId && !this.peers.has(status.hostId)) {
        const hostPeerInfo: PeerInfo = {
          id: status.hostId,
          name: 'Host',
          connection: null as any,
          isConnected: true,
          lastSeen: Date.now(),
          dataChannel: undefined
        };
        this.peers.set(status.hostId, hostPeerInfo);
        this.callbacks?.onPeerJoin?.(hostPeerInfo);
      }
      
      console.log(`👥 Client: Connected to host ${status.hostId}`);
    }
  }

  // Real HTTP-based signaling 
  private stopPolling() {
    if (this.pollingInterval) {
      clearTimeout(this.pollingInterval); // Changed from clearInterval to clearTimeout
      this.pollingInterval = null;
    }
  }

  private startStatelessPolling() {
    if (this.pollingInterval || !this.sessionCode) return;
    
    console.log(`🔄 ${this.role}: Starting adaptive stateless polling for session ${this.sessionCode}`);
    
    const poll = async () => {
      try {
        const response = await fetch(`${this.signalingUrl}?action=get-messages&sessionCode=${this.sessionCode}&peerId=${this.localPeerId}`, {
          headers: this.getAuthHeaders()
        });
        
        if (!response.ok) {
          if (response.status === 404) {
            console.log(`⚠️ ${this.role}: Session ${this.sessionCode} not found - stopping polling`);
            this.updateState('disconnected');
            this.stopPolling();
            return;
          }
          throw new Error(`Polling failed: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (data.messages && data.messages.length > 0) {
          console.log(`📨 ${this.role}: Received ${data.messages.length} messages from signaling server`);
          
          // Process all messages - simplified approach
          for (const message of data.messages) {
            await this.handleSignalingMessage(message);
          }
          
          this.lastMessageTime = Date.now();
        }
        
        // Simple fixed polling interval - removed verbose logging
        // Removed unused timeSinceLastMessage calculation
        
        // Don't trigger state-based fallback - this was causing the performance issues
        
      } catch (error) {
        console.error(`❌ ${this.role}: Stateless polling error:`, error);
        // Don't stop polling on transient errors, just log them
      }
      
      // Poll every 3 seconds - balanced between responsiveness and rate limiting
      if (this.pollingInterval) {
        this.pollingInterval = window.setTimeout(poll, 3000); // 3 second interval
      }
    };
    
    // Start first poll immediately
    this.pollingInterval = window.setTimeout(poll, 1000); // First poll after 1 second
  }

  // Removed unused checkStateBasedFallback method

  // Removed unused triggerFullStateSync method

  private async sendSignalingMessage(message: any, targetPeer?: string) {
    if (!this.sessionCode) {
      console.error('❌ No session code for signaling');
      return;
    }
    
    // Removed verbose signaling logs
    // console.log(`📤 ${this.role}: Sending signaling message:`, message.type, targetPeer ? `to ${targetPeer}` : 'broadcast');
    
    try {
      const messageData = {
        ...message,
        from: this.localPeerId,
        to: targetPeer,
        sessionCode: this.sessionCode,
        timestamp: Date.now()
      };
      
      // Removed verbose payload log
      // console.log(`🌐 Sending to signaling server:`, { action: 'send-message', sessionCode: this.sessionCode, peerId: this.localPeerId, message: messageData });
      
      const response = await fetch(this.signalingUrl, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          action: 'send-message',
          sessionCode: this.sessionCode,
          peerId: this.localPeerId,
          message: messageData
        })
      });
      
      if (!response.ok) {
        throw new Error(`Signaling failed: ${response.status}`);
      }
      
      await response.json(); // Consume response
      // Signal sent - removed verbose log
      // console.log(`✅ Signal sent successfully`);
      
    } catch (error) {
      console.error('❌ Failed to send signaling message:', error);
      // Don't show user errors for temporary signaling issues - these are handled by reconnection
      // Only show errors for persistent failures that can't be recovered
    }
  }

  private async handleSignalingMessage(message: any) {
    if (message.from === this.localPeerId) return; // Ignore our own messages
    
    // Check if message is for us (targeted) or if we should handle it
    if (message.to && message.to !== this.localPeerId) return; // Not for us
    
    // Removed verbose message logs - only log important events
    // console.log(`📡 ${this.role}: Received signaling message:`, message.type, 'from', message.from);
    // console.log(`📡 ${this.role}: Full message structure:`, JSON.stringify(message, null, 2));
    
    const peerId = message.from;
    let peer = this.peers.get(peerId);

    if (!peer && (message.type === 'join' || message.type === 'session-created')) {
      if (message.type === 'join' && this.role === 'host') {
        console.log(`🟢 Host: New client ${peerId} wants to join session ${message.sessionCode}`);
        
        // Verify the client is joining the correct session
        if (message.sessionCode !== this.sessionCode) {
          console.log(`🟢 Host: Rejecting join request - wrong session code (${message.sessionCode} vs ${this.sessionCode})`);
          return;
        }
        
        // Create new peer connection (or recreate if this is a reconnection)
        peer = {
          id: peerId,
          name: message.name || `Client ${peerId.substring(0, 6)}`,
          connection: this.createPeerConnection(),
          isConnected: false,
          lastSeen: Date.now()
        };
        this.peers.set(peerId, peer);
        console.log(`🟢 Host: Added peer ${peerId} to peer map. Total peers: ${this.peers.size}`);

        // If we're in fallback mode, immediately mark as connected and trigger onPeerJoin
        if (this.fallbackMode) {
          console.log(`🔄 Host: In fallback mode, marking client ${peerId} as connected`);
          peer.isConnected = true;
          this.callbacks?.onPeerJoin(peer);
        } else {
          // Set up ICE candidate handling BEFORE creating offer
          peer.connection.onicecandidate = (event) => {
            if (event.candidate) {
              this.sendSignalingMessage({
                type: 'ice-candidate',
                candidate: event.candidate
              }, peerId);
            }
          };

          console.log(`🟢 Host: Creating data channel and offer for client ${peerId}`);
          // Host creates data channel
          peer.dataChannel = peer.connection.createDataChannel('chart-sync', {
            ordered: true
          });
          this.setupDataChannel(peer.dataChannel, peerId);

          // Create offer
          const offer = await peer.connection.createOffer();
          await peer.connection.setLocalDescription(offer);
          
          console.log(`🟢 Host: Sending offer to client ${peerId}`);
          this.sendSignalingMessage({
            type: 'offer',
            offer: offer
          }, peerId);
        }
      } else if (message.type === 'session-created' && this.role === 'client') {
        // Host has reconnected, we should re-establish connection
        console.log(`🔵 Client: Host ${peerId} has reconnected, re-establishing connection`);
        
        // Send join request again
        this.sendSignalingMessage({
          type: 'join',
          sessionCode: this.sessionCode,
          name: `Client ${this.localPeerId.substring(0, 6)}`
        });
      }
    }

    // Update lastSeen for any signaling message
    if (peer) {
      peer.lastSeen = Date.now();
    }

    switch (message.type) {
      case 'offer':
        if (this.role === 'client') {
          // Create peer object for host if it doesn't exist
          if (!peer) {
            console.log(`🔵 Client: Creating peer connection for host ${peerId}`);
            peer = {
              id: peerId,
              name: 'Host',
              connection: this.createPeerConnection(),
              isConnected: false,
              lastSeen: Date.now()
            };
            this.peers.set(peerId, peer);
            console.log(`🔵 Client: Added host ${peerId} to peer map. Total peers: ${this.peers.size}`);
          } else if (peer.connection.connectionState === 'failed' || peer.connection.connectionState === 'closed') {
            // Existing peer needs new connection
            console.log(`🔵 Client: Recreating connection for host ${peerId}`);
            peer.connection.close();
            peer.connection = this.createPeerConnection();
            peer.isConnected = false;
          }

          console.log(`🔵 Client: Received offer from host ${peerId}, creating answer...`);
          await peer.connection.setRemoteDescription(message.offer);
          
          // Set up data channel for incoming connection
          peer.connection.ondatachannel = (event) => {
            console.log(`🔵 Client: Data channel received from host`);
            peer!.dataChannel = event.channel;
            this.setupDataChannel(peer!.dataChannel, peerId);
          };

          const answer = await peer.connection.createAnswer();
          await peer.connection.setLocalDescription(answer);
          
          console.log(`🔵 Client: Sending answer to host ${peerId}`);
          this.sendSignalingMessage({
            type: 'answer',
            answer: answer
          }, peerId);
          
          // Set up ICE candidate handling
          peer.connection.onicecandidate = (event) => {
            if (event.candidate) {
              this.sendSignalingMessage({
                type: 'ice-candidate',
                candidate: event.candidate
              }, peerId);
            }
          };
        }
        break;

      case 'answer':
        if (this.role === 'host' && peer) {
          console.log(`🟢 Host: Received answer from client ${peerId}`);
          await peer.connection.setRemoteDescription(message.answer);
          
          // ICE candidate handling is already set up when peer was created
        }
        break;

      case 'ice-candidate':
        if (peer) {
          const candidate = message.candidate;
          console.log(`📡 ${this.role}: Received ICE candidate from ${peerId}: ${candidate.type || 'unknown'} | ${candidate.protocol || 'unknown'} | priority: ${candidate.priority || 'unknown'}`);
          console.log(`📡 Full remote candidate: ${candidate.candidate}`);
          
          // Parse candidate string for better analysis
          const candidateStr = candidate.candidate;
          if (candidateStr.includes('typ host')) {
            console.log('🏠 Host candidate (local network)');
          } else if (candidateStr.includes('typ srflx')) {
            console.log('🌐 STUN candidate (public IP via STUN server)');
          } else if (candidateStr.includes('typ relay')) {
            console.log('🔄 TURN candidate (relayed via TURN server)');
          }
          
          try {
            await peer.connection.addIceCandidate(message.candidate);
            console.log(`✅ Successfully added ICE candidate from ${peerId}`);
          } catch (error) {
            console.error(`❌ Failed to add ICE candidate from ${peerId}:`, error);
          }
        } else {
          console.error(`❌ Received ICE candidate from unknown peer ${peerId}`);
        }
        break;
      
      case 'chart-data':
        // Handle fallback mode chart updates via signaling server
        console.log('📊 Fallback: Received chart update via signaling server');
        console.log('📊 Fallback: message.data structure:', JSON.stringify(message.data, null, 2));
        if (message.data) {
          // In fallback mode, message.data contains the WebRTCMessage
          this.handleDataChannelMessage(message.data, peerId);
        }
        break;

      case 'chart-request':
        // Handle fallback mode chart requests from clients
        console.log('📡 Fallback: Received chart request from client');
        if (this.role === 'host' && message.data?.requestFullChart) {
          console.log('📡 Host: Sending full chart to requesting client');
          // Trigger onPeerJoin to send full chart
          const requestingClient: PeerInfo = {
            id: peerId,
            name: `Client ${peerId.substring(0, 6)}`,
            connection: {} as RTCPeerConnection, // Dummy connection for fallback
            isConnected: true,
            lastSeen: Date.now()
          };
          this.callbacks?.onPeerJoin(requestingClient);
        }
        break;

      case 'leave':
        // Handle peer leaving the session
        console.log(`👋 ${this.role}: Peer ${peerId} is leaving the session`);
        this.removePeer(peerId);
        break;
      
      case 'heartbeat':
        // Handle heartbeat from client - update lastSeen to prevent timeout
        console.log(`💓 ${this.role}: Received heartbeat from peer ${peerId}`);
        if (peer) {
          peer.lastSeen = Date.now();
        } else {
          // Peer sent heartbeat but we don't have them - they might have reconnected
          console.log(`💓 Unknown peer ${peerId} sent heartbeat - might be reconnecting`);
        }
        break;
    }
  }

  // Public API
  public setCallbacks(callbacks: WebRTCCallbacks) {
    this.callbacks = callbacks;
  }

  public async createSession(): Promise<string> {
    try {
      console.log(`🟢 Host: Creating new session...`);
      this.updateState('creating');
      this.role = 'host';
      this.sessionCode = this.generateSessionCode();
      
      // Reset leftPeers set for new session
      this.leftPeers.clear();
      
      // Create session with stateless signaling server
      const response = await fetch(this.signalingUrl, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          action: 'create-session',
          sessionCode: this.sessionCode,
          peerId: this.localPeerId
        })
      });
      
      console.log(`🌐 Create session response:`, response.status, response.statusText);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Failed to create session: ${response.status} - ${errorText}`);
        throw new Error(`Failed to create session: ${response.status}`);
      }
      
      // No connection ID needed in stateless mode
      console.log(`🟢 Host: Session created with code ${this.sessionCode}, starting stateless polling...`);
      
      // Start stateless message polling
      this.startStatelessPolling();
      
      this.startHeartbeat();
      this.updateState('hosting'); // Host should be in hosting state, not connected
      
      // Start fallback timeout - if no clients connect via P2P in 30 seconds, enable fallback
      this.startFallbackTimeout();
      
      return this.sessionCode;
    } catch (error) {
      console.error('Failed to create session:', error);
      this.updateState('error');
      this.callbacks?.onError('Failed to create session');
      throw error;
    }
  }

  public async joinSession(sessionCode: string): Promise<void> {
    try {
      console.log(`🔵 Client: Attempting to join session ${sessionCode}`);
      this.updateState('joining');
      this.role = 'client';
      this.sessionCode = sessionCode;
      
      // Reset leftPeers set for new session
      this.leftPeers.clear();
      
      // Join session with stateless signaling server
      const response = await fetch(this.signalingUrl, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          action: 'join-session',
          sessionCode: sessionCode,
          peerId: this.localPeerId
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to join session: ${response.status}`);
      }
      
      console.log(`🔵 Client: Successfully joined session ${sessionCode}, starting stateless polling...`);
      
      // IMPORTANT: Send join message to host via signaling so host knows we joined
      this.sendSignalingMessage({
        type: 'join',
        sessionCode: this.sessionCode,
        name: `Client ${this.localPeerId.substring(0, 6)}`
      });
      
      // Start stateless message polling
      this.startStatelessPolling();
      
      this.startHeartbeat();
      console.log(`🔵 Client: Started polling for session updates...`);
      
      // Start fallback timeout - if P2P connection fails in 30 seconds, enable fallback
      this.startFallbackTimeout();
      
      // State will be updated when data channel opens
      
    } catch (error) {
      console.error('Failed to join session:', error);
      this.updateState('error');
      this.callbacks?.onError('Failed to join session');
      throw error;
    }
  }

  public leaveSession() {
    console.log('🚪 leaveSession called - cleaning up...');
    this.updateState('disconnected');
    
    // Clear fallback timeout
    this.clearFallbackTimeout();
    
    // Reset message deduplication state
    this.processedMessageIds.clear();
    
    // Reset polling state
    this.lastMessageTime = 0;
    
    // Reset state-based sync state
    this.syncMode = 'edit-based';
    
    // IMPORTANT: Reset fallback mode for next session
    this.fallbackMode = false;
    
    // IMPORTANT: Clear leftPeers for next session
    this.leftPeers.clear();
    
    // Notify peers we're leaving via signaling message
    // Send leave message via signaling (using sendBeacon for reliability during page unload)
    if (this.sessionCode) {
      const leaveMessage = JSON.stringify({
        action: 'send-message',
        sessionCode: this.sessionCode,
        peerId: this.localPeerId,
        message: {
          type: 'leave',
          from: this.localPeerId,
          peerId: this.localPeerId,
          sessionCode: this.sessionCode,
          timestamp: Date.now()
        }
      });
      
      // Use sendBeacon for reliability during page unload (beforeunload event)
      // Falls back to fetch if sendBeacon is not available
      if (navigator.sendBeacon) {
        const blob = new Blob([leaveMessage], { type: 'application/json' });
        navigator.sendBeacon(this.signalingUrl, blob);
        console.log('📤 Sent leave message via sendBeacon');
      } else {
        this.sendSignalingMessage({
          type: 'leave',
          peerId: this.localPeerId
        });
      }
    }
    
    // IMPORTANT: Also notify server to remove us from session (also via sendBeacon)
    if (this.sessionCode) {
      const leaveSessionData = JSON.stringify({
        action: 'leave-session',
        sessionCode: this.sessionCode,
        peerId: this.localPeerId
      });
      
      if (navigator.sendBeacon) {
        const blob = new Blob([leaveSessionData], { type: 'application/json' });
        navigator.sendBeacon(this.signalingUrl, blob);
        console.log('📤 Sent leave-session via sendBeacon');
      } else {
        fetch(this.signalingUrl, {
          method: 'POST',
          headers: this.getAuthHeaders(),
          body: leaveSessionData
        }).catch(err => console.log('Failed to notify server of leave:', err));
      }
    }

    // Close all peer connections
    for (const peer of this.peers.values()) {
      peer.connection.close();
    }
    this.peers.clear();

    this.stopHeartbeat();
    this.stopPolling();
    this.stopSessionStatusPolling();
    this.stopChartSyncHeartbeat();
    
    // Clean up signaling connection (legacy)
    if (this.connectionId) {
      fetch(this.signalingUrl, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          action: 'disconnect',
          connectionId: this.connectionId
        })
      }).catch(err => console.log('Failed to notify server of disconnect:', err));
      
      this.connectionId = null;
    }
    
    // Reset session code after cleanup
    this.sessionCode = '';
    
    // Reset role for next session
    this.role = null;
    
    console.log('🚪 leaveSession complete - state reset to idle');
    this.updateState('idle');
  }

  public sendMessage(message: Omit<WebRTCMessage, 'timestamp'>, targetPeerId?: string) {
    const fullMessage: WebRTCMessage = {
      ...message,
      timestamp: Date.now(),
      senderId: this.localPeerId
    };

    if (this.fallbackMode) {
      // In fallback mode, send chart updates through signaling server
      console.log('📡 Fallback: Sending chart update via signaling server');
      this.sendSignalingMessage({
        type: 'chart-data',
        data: fullMessage
      }, targetPeerId);
      return;
    }

    if (targetPeerId) {
      // Send to specific peer
      const peer = this.peers.get(targetPeerId);
      if (peer?.dataChannel?.readyState === 'open') {
        peer.dataChannel.send(JSON.stringify(fullMessage));
      }
    } else {
      // Broadcast to all peers
      for (const peer of this.peers.values()) {
        if (peer.dataChannel?.readyState === 'open') {
          peer.dataChannel.send(JSON.stringify(fullMessage));
        }
      }
    }
  }

  public broadcastChartUpdate(chartData: Partial<SavedChartState>) {
    if (this.role === 'host') {
      // FORCED UPDATE: Always send incremental updates - no more mode checking
      console.log('📤 Host: Broadcasting chart update (FORCED SIMPLIFIED mode)');
      this.sendMessage({
        type: 'chart-update',
        data: chartData
      });
    }
  }

  public broadcastFullChart(chartData: SavedChartState) {
    if (this.role === 'host') {
      // FORCED UPDATE: Always use 'chart-full' - no more state-based complexity
      console.log(`📤 Host: Broadcasting full chart (FORCED SIMPLIFIED mode)`);
      
      this.sendMessage({
        type: 'chart-full',
        data: chartData
      });
      
      // Update sent successfully
    }
  }

  public getConnectedPeers(): PeerInfo[] {
    return Array.from(this.peers.values()).filter(peer => peer.isConnected);
  }

  public getSessionCode(): string {
    return this.sessionCode;
  }

  public getRole(): WebRTCRole {
    return this.role;
  }

  public getState(): ConnectionState {
    return this.state;
  }

  public getSyncMode(): 'edit-based' {
    return this.syncMode;
  }

  public isInStateBasedMode(): boolean {
    return false; // Always false now - no more state-based mode
  }
}

// Export singleton instance
export const webRTCService = new WebRTCService();
export default webRTCService;